import { BehaviorSubject, Subject } from 'rxjs';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { ScrollService } from 'src/app/shared/services/scroll.service';
import { TaskListCode } from 'src/app/tasks/shared/enums';
import { environment } from 'src/environments/environment';

import { AJSTasksService } from '../../../admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from '../../core/alcmeon-context/alcmeon-context.service';
import { AbstractComponentWithScrollContainer } from '../../shared/components/abstract-component-with-scroll-container';
import { SingleMessages } from '../../shared/components/single-message-editor/single-messages';
import { EditorConfig } from '../../shared/editor-config';
import { MessageSender, Site, TaskState } from '../../shared/models/misc.model';
import { BasicService, RoutePaths } from '../../shared/services/basic.service';
import {
  AnswersAndAfterSendAction,
  CustomFieldsContext,
  LogAnswerRequest,
  ProcessedTaskInfo,
  Task,
  TaskForProcessingResponse,
} from './model';
import { WhatsappTemplate } from './models';
import { AIAssistantAnswerReliabilityEnum } from './models/ai-assistant-answer-reliability.enum';
import { TagsContext } from './tags-context.class';
import { TaskProcessService } from './task-process.service';

/**
 * This class holds data that needs to be shared between the task process page and its various components and modals.
 * It helps keeping the number of input and output parameters to the components under control.
 * It also includes functionnality used by the components and modals, such as going back to the tasks list
 */
export class TaskProcessContext {
  allAnnotationIds: string[];
  allHelpRequestAndResponseIds: string[];
  crmIFrameDidClose$ = new Subject<void>();
  doBlinkBubble$ = new Subject<string>(); // taskOrActionIds to blink
  doScrollToTaskOrAction$ = new Subject<{ taskOrActionId: string; shouldBlink: boolean }>();
  editorConfig: EditorConfig;
  from: MessageSender;
  isCRMMode: boolean; // in CRM mode, the back button disappears, and we never navigate back to the task list
  isLatestTaskTooOldForReply = false; // 7 days limit for Messenger, 30 days for BZC, etc
  isLeavingPage = false; // work-around for a graphical glitch when transitionning back to the task list
  isProcessedTaskLoaded$ = new BehaviorSubject<boolean>(false);
  isPushboxMode = false; // In push mode, the user cannot release the task
  isReadOnlyMode = false; // when the procesed task is not assigned to the logged user, a "process this message" button replaces the editor
  isWhatsappTemplateModeMandatory?: boolean;
  ngOnDestroy$ = new Subject<void>();
  previousOnHoldNotByBotIds: string[]; // ['<id>:task']
  processedMessageHasBeenDeleted = false; // Messages may have been deleted on the social network
  processedTaskId: number; // 12345
  processedTaskInfo: ProcessedTaskInfo;
  processedTaskOrActionId: string; // '12345:task'
  refererTaskListCode: TaskListCode; // the task list we should return to after processing this task
  replyAsSiteId: number;
  singleMessages: SingleMessages;
  site: Site | SiteV2;
  tagsContext: TagsContext;
  taskProcessPageDidEnter$ = new BehaviorSubject<boolean>(false);
  taskProcessPageWithScrollContainer: AbstractComponentWithScrollContainer;
  tooOldForReplyTranslationKey = ''; // e.g. 'm/task-process/task_is_too_old'
  whatsappTemplates?: WhatsappTemplate[]; // not present in engagement page.

  /**
   * In the engagement mode the custom field must be retrieved at the root and do not depend on the any task in proccess.
   */
  customFieldContext?: CustomFieldsContext;

  /**
   * The AI.SSISTANT generates an answer. This one can be rejected or accepted and we need to track the activity for the statistics.
   */
  aiAssistantSuggestedAnswer?: {
    id: string;
    suggestion: string;
    status: AIAssistantAnswerReliabilityEnum.REJECTED | null;
  };

  // Flag to indicate a conversation is closed. Specific to Apple Messages.
  isConversationClosedBS?: BehaviorSubject<boolean> = new BehaviorSubject(false);

  engagement = false;

  private isReplyToPublicInPrivate = false;
  private isLinkShortenerEnabled: boolean;

  constructor(
    private basicService: BasicService,
    private scrollService: ScrollService,
    private alcmeonContext: AlcmeonContextService,
    private taskProcessService: TaskProcessService,
    private ajsTasksService: AJSTasksService
  ) {}

  initWithTask(response: TaskForProcessingResponse) {
    if (!this.engagement) {
      this.updateWithTask(response);
      this.resetEditorConfig();
    } else {
      response.isWhatsappTemplateModeMandatory = true;
    }
    this.isProcessedTaskLoaded$.next(true);
  }

  updateWithTask(response: TaskForProcessingResponse) {
    this.processedTaskOrActionId = response.processingTask.id;
    this.from = response.from;
    this.site = response.site;
    this.processedTaskInfo = response.processingTask;
    this.tagsContext = new TagsContext(
      response.tagGroups,
      response.routingTagGroup,
      this.processedTaskId,
      this.processedTaskInfo.state.taskStateCode,
      this
    );
    this.previousOnHoldNotByBotIds = response.previousOnHoldNotByBotIds || [];
    this.allAnnotationIds = response.allAnnotationIds;
    this.allHelpRequestAndResponseIds = response.allHelpRequestAndResponseIds;
    this.isWhatsappTemplateModeMandatory = response.isWhatsappTemplateModeMandatory;

    this.processedMessageHasBeenDeleted = (response.tasksOrActionsById[response.processingTask.id] as Task).isDeleted;
    this.isLatestTaskTooOldForReply = !response.processingTask.isTooOldToReply.canReply;
    this.tooOldForReplyTranslationKey = response.processingTask.isTooOldToReply.reason;
    this.isLinkShortenerEnabled = response.isLinkShortenerEnabled;
    this.updateIsReadOnly();
  }

  updateOnProcessedTaskReloaded(reloadedTask: Task) {
    this.tagsContext.updateTagsValue(reloadedTask.routingTagId, reloadedTask.tagIds);
    this.processedTaskInfo.state.taskStateCode = reloadedTask.taskStateCode;
    this.processedTaskInfo.state.adminUser = reloadedTask.adminUser;
    this.updateIsReadOnly();
  }

  isPublicAndProcessedMessageHasBeenDeleted() {
    return this.processedMessageHasBeenDeleted && this.isPublic();
  }

  setSingleMessages(singleMessages: SingleMessages) {
    this.singleMessages = singleMessages;
  }

  areAllEditorsEmpty() {
    if (!this.singleMessages) {
      return true;
    }
    return this.singleMessages.areAllEditorsEmpty();
  }

  destroy() {
    if (this.tagsContext) {
      this.tagsContext.destroy();
    }
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();

    this.taskProcessPageDidEnter$.complete();
    this.isProcessedTaskLoaded$.complete();
    this.doBlinkBubble$.complete();
    this.isConversationClosedBS?.complete();
  }

  isAssignedToMe() {
    const state = this.processedTaskInfo.state;
    return state.taskStateCode === 'assigned' && this.alcmeonContext.isMe(state.adminUser);
  }

  isPrivate() {
    return this.processedTaskInfo?.isPrivate;
  }

  mustSwitchFromPublicToPrivate() {
    return this.processedTaskInfo?.mustSwitchFromPublicToPrivate;
  }

  isPublic() {
    return this.processedTaskInfo ? !this.isPrivate() : undefined;
  }

  isReplyingInPrivate() {
    return this.isPrivate() || this.isReplyToPublicInPrivate || this.mustSwitchFromPublicToPrivate();
  }

  isProcessedTask(taskOrActionId) {
    return this.processedTaskOrActionId === taskOrActionId;
  }

  isPreviousOnHoldNotByBot(taskOrActionId) {
    return (this.previousOnHoldNotByBotIds || []).includes(taskOrActionId);
  }

  removeFromPreviousOnHoldNotByBot(taskOrActionId) {
    const index = (this.previousOnHoldNotByBotIds || []).indexOf(taskOrActionId);
    if (index >= 0) {
      this.previousOnHoldNotByBotIds.splice(index, 1);
    }
  }

  scrollToProcessedTask() {
    this.scrollToTaskOrAction(this.processedTaskOrActionId, false);
  }

  scrollToTaskOrAction(taskOrActionId: string, shouldBlink: boolean) {
    this.doScrollToTaskOrAction$.next({ taskOrActionId, shouldBlink });
  }

  scrollToBottom() {
    // give angular a chance to update the DOM
    setTimeout(() => this.scrollService.scrollToBottom(this.taskProcessPageWithScrollContainer));
  }

  /**
   * If the processed task is not assigned to the logged user, the current task state is not changed;
   * If the previous state of the processed task was 'pending',
   * or is in pushbox (the display of button is conditionned in ArchiveAndOtherActionsComponent),
   * release it (that is, move it back to its previous state);
   * If the previous state is something else, open a modal that offers to move it back to 'pending'
   * or to its previous state.
   *
   * In any case, we go back to the task list after that.
   */
  async backToTaskListRequested(event: MouseEvent) {
    const previousState = this.processedTaskInfo.state.previousStateCode;
    if (this.isReadOnlyMode) {
      this.backToTaskList();
    } else if (!previousState || previousState === TaskState.Pending || this.isPushboxMode) {
      this.taskProcessService.releaseTask(this.processedTaskId).subscribe(() => this.backToTaskList());
    } else {
      const modalResult = await this.basicService.selectInPopover(event, this.nextStateOptions(previousState));
      if (modalResult.data) {
        modalResult.data.httpObservable.subscribe(() => this.backToTaskList());
      }
    }
  }

  /**
   * Allows to go back to the tasks list. By default it redirects to the queueId 'assigned-to-you'.
   * @remarks The state param queueId is hardcoded in the abstract state 'auth.alcmeon.inbox.list' but it can be overwritten in 'auth.alcmeon.inbox.list.tasks'
   * @param queueId The queueId to display.
   */
  backToTaskList(queueId?: string) {
    if (environment.bundledWithAngularJSApp) {
      // use angularjs task service to navigate back to the tasks list
      this.ajsTasksService.backToTasksList(queueId);
    } else {
      // hide the toolbar before ionic router transitions back to the task list,
      // otherwise the translucent backdrops stays over the task list for about a second
      this.isLeavingPage = true;
      setTimeout(() => {
        this.basicService.navigateBack(`${RoutePaths.TasksListsTabs}/${this.refererTaskListCode}`);
      });
    }
  }

  publicContextHasTreeStructure() {
    return this.site.type === SiteTypeEnum.Facebook;
  }

  buildLogAnswserRequest(answersAndAfterSendAction: AnswersAndAfterSendAction): LogAnswerRequest {
    return {
      taskId: this.processedTaskId,
      replyAs: {
        id: this.replyAsSiteId,
      },
      replyTo: this.from,
      replyToUserIds: this.processedTaskInfo.replyToList.filter(user => user.isSelected).map(user => user.id),
      isPrivate: this.isReplyingInPrivate(),
      answers: answersAndAfterSendAction.answers,
    };
  }

  switchReplyIn(isPrivate: boolean) {
    this.isReplyToPublicInPrivate = isPrivate;
    this.resetEditorConfig();
    this.singleMessages.switchReplyIn(this.editorConfig);
    if (this.isPublic() && this.isReplyToPublicInPrivate) {
      this.singleMessages.forceHideAttachments(true);
    }
  }

  shouldOfferToTriggerSurvey() {
    const surveyInfo = this.processedTaskInfo.manuallySendableSurveys;
    return (
      surveyInfo.hasManuallySendableSurvey &&
      surveyInfo.tagRestrictions.some(restrictionsForOneSurvey =>
        this.tagsContext.matchesRestrictions(restrictionsForOneSurvey)
      )
    );
  }

  private updateIsReadOnly() {
    if (this.isCRMMode) {
      // In CRM mode we want to always display the text input area
      // (assign will be made on demand when the user clicks on send).
      // When not in CRM mode, we want to enter readonly mode if the task is not
      // assigned to us.
      this.isReadOnlyMode = false;
    } else {
      this.isReadOnlyMode = !this.isAssignedToMe();
    }
  }

  private resetEditorConfig() {
    this.editorConfig = new EditorConfig((this.site as SiteV2).typeName || (this.site as Site).type, {
      isPrivate: this.isReplyingInPrivate(),
      isSpellCheckerEnabledForCompany: this.alcmeonContext.isSpellCheckerEnabledForCompany(),
      isEngagement: this.engagement,
    });
    this.editorConfig.spellCheckerLanguageCode = this.tagsContext.getLangageCode();
    this.editorConfig.isLinkShortenerEnabled = this.isLinkShortenerEnabled;
  }

  /**
   * builds the options to be passed to the select modal so that the user can choose
   * which state the task should go to before going back to the task list.
   * Note that this shouldn't be called if the previous state is Pending
   * because in that case both options will do the same thing
   * (hence there is no need to show a select modal in that case)
   */
  private nextStateOptions(previousState: string) {
    return [
      {
        httpObservable: this.taskProcessService.moveTaskToPending(this.processedTaskId),
        label: this.basicService.translate(`m/task-process/move-back-to/pending`),
      },
      {
        httpObservable: this.taskProcessService.releaseTask(this.processedTaskId),
        label: this.basicService.translate(`m/task-process/move-back-to/${previousState}`),
      },
    ];
  }
}
